import { useCallback, useState } from 'react';

import { FieldPath, RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { popModal } from '@/redux/modals/actions';
import { submitWithTrimming } from '@/util/util';
import { errorMessage } from '@/util/validation/helpers';

type Props = {
  onEdit: (data: Record<string, string | number>, onError?: () => void) => void;
  title: string;
  primaryButtonText?: string;
  fields: {
    fieldName: string;
    fieldValue?: string | number;
    rules?: Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  }[];
};

type FormValues = Record<string, string | number>;

// make sure that the 'fields' you will use are included in the translations json files
/**
 * Modal that enables editing multiple rule attributes while maintaining validation and translations.
 *
 * @param props - Component properties for configuring editable fields and submission handlers.
 * @returns The edit rule dialog element.
 */
export default function ModalEditMultipleValues({
  fields,
  onEdit,
  title,
  primaryButtonText,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const formMethods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: fields.reduce<Record<string, string | number>>((acc, e) => {
      return { ...acc, [e.fieldName]: e.fieldValue };
    }, {}),
  });
  const {
    control,
    formState: { errors, isValid },
  } = formMethods;

  const onSubmit = useCallback(
    async (data) => {
      setIsLoading(true);
      const onError = () => setIsLoading(false);
      onEdit(data, onError);
    },
    [onEdit]
  );

  const handleClose = useCallback(() => {
    dispatch(popModal());
  }, [dispatch]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent className="gap-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription />
        </DialogHeader>

        <Form {...formMethods}>
          <form
            className="space-y-6"
            onSubmit={submitWithTrimming(formMethods, onSubmit)}
            autoComplete="off"
          >
            {fields.map(({ fieldName, fieldValue = '', rules }, index) => {
              const fieldErrorMessage = errorMessage({ field: errors[fieldName], ...rules });

              return (
                <FormField
                  key={fieldName}
                  control={control}
                  name={fieldName as FieldPath<FormValues>}
                  rules={rules}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t(`common.${fieldName}`)}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value?.toString() ?? ''}
                          placeholder={fieldValue?.toString() ?? ''}
                          autoFocus={index === 0}
                        />
                      </FormControl>
                      <FormMessage>{fieldErrorMessage}</FormMessage>
                    </FormItem>
                  )}
                />
              );
            })}

            <DialogFooter className="sm:flex-row-reverse sm:justify-start">
              <Button type="submit" disabled={!isValid} isLoading={isLoading}>
                {primaryButtonText || t('common.change')}
              </Button>
              <Button type="button" variant="ghost-neutral" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
